import { useParams, useRouter, useSearchParams } from "next/navigation";

import { useHomeRoute } from "@/hooks/use-home-route";

import { getSafeReturnTo } from "@/lib/redirect-utils";

/**
 * Returns a function that navigates to the user's post-auth destination:
 * the `returnTo` query param if present and safe, otherwise the provided
 * `fallback` path, otherwise the user's home route — the tenant's configured
 * home page, or settings for a role that reaches no console page.
 *
 * Used by gate pages (login, sign-up, onboarding, clinic-setup) on
 * completion and by recovery pages (access-denied,
 * insufficient-permissions) to land on a page the user can actually view.
 * Pass a `fallback` for contexts that don't use the tenant home — e.g.,
 * the patient portal, where the home is `/{tenant}/patient-portal`.
 */
export function useNavigateHome({ fallback }: { fallback?: string } = {}) {
  const router = useRouter();
  const params = useParams();
  const homeRoute = useHomeRoute();
  const returnTo = getSafeReturnTo(useSearchParams());

  const tenantSlug = params.tenant as string;

  return () => {
    if (returnTo) {
      router.push(returnTo);
      return;
    }
    if (fallback) {
      router.push(fallback);
      return;
    }
    router.push(`/${tenantSlug}${homeRoute}`);
  };
}
